import React from 'react';

import Emitter from 'shared/events/emitter';

type ToastVariant = 'success' | 'info' | 'warning' | 'error';

type ToastShowPayload = {
  id?: string;
  title?: string;
  message?: string;
  variant?: ToastVariant;
  durationMs?: number;
  meta?: Record<string, unknown>;
};

type ToastItem = {
  id: string;
  title: string | null;
  message: string;
  variant: ToastVariant;
};

function uid(): string {
  return `toast_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function normalizeVariant(raw: unknown): ToastVariant {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'success' || v === 'info' || v === 'warning' || v === 'error') return v;
  return 'info';
}

function variantClasses(variant: ToastVariant): string {
  if (variant === 'success') return 'border-success/30 bg-success/10 text-success';
  if (variant === 'warning') return 'border-warning/30 bg-warning/10 text-warning';
  if (variant === 'error') return 'border-destructive/30 bg-destructive/10 text-destructive';
  return 'border-border bg-card text-card-foreground';
}

function variantIcon(variant: ToastVariant): string {
  if (variant === 'success') return 'bi-check-circle';
  if (variant === 'warning') return 'bi-exclamation-triangle';
  if (variant === 'error') return 'bi-x-circle';
  return 'bi-info-circle';
}

export default function ToastHost() {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const timersRef = React.useRef(new Map<string, number>());

  const remove = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer != null) window.clearTimeout(timer);
    timersRef.current.delete(id);
  }, []);

  React.useEffect(() => {
    const onShow = (payload: ToastShowPayload) => {
      const message = payload?.message != null ? String(payload.message).trim() : '';
      if (!message) return;

      const id = payload?.id != null ? String(payload.id) : uid();
      const title = payload?.title != null ? String(payload.title).trim() : '';
      const variant = normalizeVariant(payload?.variant);
      const durationMsRaw = payload?.durationMs;
      const durationMs = Number.isFinite(Number(durationMsRaw)) ? Math.max(0, Number(durationMsRaw)) : 6000;

      setToasts((prev) => {
        // Keep newest at the bottom; cap to avoid runaway UI.
        const next = [...prev, { id, title: title || null, message, variant }].slice(-5);
        return next;
      });

      if (durationMs > 0) {
        const timer = window.setTimeout(() => remove(id), durationMs);
        timersRef.current.set(id, timer);
      }
    };

    Emitter.on('TOAST.SHOW', onShow);
    return () => {
      Emitter.off('TOAST.SHOW', onShow);
      for (const timer of timersRef.current.values()) window.clearTimeout(timer);
      timersRef.current.clear();
    };
  }, [remove]);

  if (!toasts.length) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[110] w-[calc(100vw-2rem)] max-w-md pointer-events-none"
      aria-live="polite"
      aria-relevant="additions"
    >
      <div className="flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto rounded-lg border shadow-sm backdrop-blur-sm ${variantClasses(t.variant)}`}
          >
            <div className="flex items-start gap-3 p-3">
              <div className="mt-0.5 flex-shrink-0 opacity-80">
                <i className={`bi ${variantIcon(t.variant)}`} />
              </div>
              <div className="min-w-0 flex-1">
                {t.title ? <div className="text-sm font-semibold leading-5">{t.title}</div> : null}
                <div className="text-sm leading-5 text-foreground/90">{t.message}</div>
              </div>
              <button
                type="button"
                onClick={() => remove(t.id)}
                className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent/40 hover:bg-accent/60 text-accent-foreground transition-colors"
                aria-label="Dismiss notification"
              >
                <i className="bi bi-x" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

